import { getHighPriorityConnection } from '@src/chains/solana/anchor/setup'
import { solanaLogger as logger } from '@src/utils/logger'

/**
 * Priority fee levels for Solana transactions
 */
export enum PriorityFeeLevel {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    MAXIMUM = 'maximum',
}

/**
 * Response structure from QuickNode's qn_estimatePriorityFees RPC method
 */
interface PriorityFeeResponse {
    jsonrpc: string
    id: number
    result: {
        context: {
            slot: number
        }
        recommended: number
        per_compute_unit: {
            extreme: number
            high: number
            medium: number
            low: number
            percentiles: Record<string, number>
        }
        per_transaction: {
            extreme: number
            high: number
            medium: number
            low: number
            percentiles: Record<string, number>
        }
    }
}

/**
 * Default priority fee to use as fallback if API call fails
 */
const DEFAULT_PRIORITY_FEE = 35_000_000

/**
 * Fetches the current recommended priority fee from Solana's getRecentPrioritizationFees RPC method
 *
 * @param level - The priority level to use (low, medium, high, maximum)
 * @param accountAddresses - Optional array of account addresses to get specific fees for
 * @returns The recommended priority fee in microLamports
 */
/**
 * Fetches the recommended priority fee from QuickNode's qn_estimatePriorityFees API
 *
 * @param level - The priority level to use (low, medium, high, maximum)
 * @param accountAddress - Optional account address to get specific fees for
 * @param forceFallback - If true, forces the use of the fallback RPC URL
 * @returns The recommended priority fee in microLamports
 */
export async function getPriorityFee(
    level: PriorityFeeLevel = PriorityFeeLevel.HIGH,
    accountAddress?: string
): Promise<number> {
    try {
        const connection = getHighPriorityConnection(true)
        const rpcEndpoint = connection.rpcEndpoint

        // Call QuickNode's qn_estimatePriorityFees RPC method
        const response = await fetch(rpcEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'qn_estimatePriorityFees',
                params: {
                    last_n_blocks: 100,
                    account: accountAddress,
                    api_version: 2,
                },
            }),
        })

        if (!response.ok) {
            throw new Error(`QuickNode API request failed with status ${response.status}`)
        }

        const data = (await response.json()) as PriorityFeeResponse

        // Extract the appropriate fee based on the requested level
        let priorityFee: number

        switch (level) {
            case PriorityFeeLevel.LOW:
                priorityFee = data.result.per_compute_unit.low
                break
            case PriorityFeeLevel.MEDIUM:
                priorityFee = data.result.per_compute_unit.medium
                break
            case PriorityFeeLevel.HIGH:
                // For HIGH priority, use the 95th percentile for better transaction inclusion
                priorityFee = data.result.per_compute_unit.percentiles['95']
                break
            case PriorityFeeLevel.MAXIMUM:
                priorityFee = data.result.per_compute_unit.extreme
                break
            default:
                // Fallback to high
                priorityFee = data.result.per_compute_unit.high
        }

        // Ensure we have a positive fee value
        if (!priorityFee || priorityFee <= 0) {
            priorityFee = DEFAULT_PRIORITY_FEE
        }

        logger.debug('[SOL] Retrieved QuickNode priority fee', {
            level,
            accountSpecified: !!accountAddress,
            fee: priorityFee,
            slot: data.result.context?.slot,
        })

        return priorityFee || DEFAULT_PRIORITY_FEE
    } catch (error) {
        logger.warn('[SOL] Failed to fetch Solana priority fee, using default', {
            error: error instanceof Error ? error.message : String(error),
            defaultFee: DEFAULT_PRIORITY_FEE,
        })

        // Return the default fee as fallback
        return DEFAULT_PRIORITY_FEE
    }
}
